/* eslint-disable no-restricted-globals */
import { useEffect, useRef } from 'react';

/**
 * Hook to preserve data on orientation change
 * Stores data in sessionStorage to survive orientation changes
 */
export function useOrientationPersistence<T>(
  key: string,
  data: T,
  enabled: boolean = true
): void {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    // Skip saving on first render to avoid overwriting loaded data
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Save data to sessionStorage
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save data to sessionStorage:', error);
    }
  }, [key, data, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (enabled) {
        try {
          sessionStorage.removeItem(key);
        } catch (error) {
          console.error('Failed to remove data from sessionStorage:', error);
        }
      }
    };
  }, [key, enabled]);
}

/**
 * Hook to load persisted data on mount
 */
export function useLoadPersistedData<T>(key: string, defaultValue: T): T {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch (error) {
    console.error('Failed to load data from sessionStorage:', error);
  }
  return defaultValue;
}

/**
 * Hook to detect orientation changes
 */
export function useOrientationChange(callback: (orientation: OrientationType) => void): void {
  useEffect(() => {
    const screenObj = window.screen;

    const handleOrientationChange = () => {
      if (screenObj.orientation) {
        callback(screenObj.orientation.type);
      }
    };

    if (screenObj.orientation) {
      screenObj.orientation.addEventListener('change', handleOrientationChange);
    } else {
      window.addEventListener('orientationchange', handleOrientationChange);
    }

    return () => {
      if (screenObj.orientation) {
        screenObj.orientation.removeEventListener('change', handleOrientationChange);
      } else {
        window.removeEventListener('orientationchange', handleOrientationChange);
      }
    };
  }, [callback]);
}
